/**
 * Algorithme BFS pour trouver les chemins et valider les déplacements
 */

import { Position, Wall, GameState, BOARD_SIZE } from './types';

interface Node {
  position: Position;
  parent: Node | null;
}

/**
 * Vérifie si un mur bloque le passage entre deux positions adjacentes
 */
export function isBlockedByWall(
  from: Position,
  to: Position,
  walls: Wall[]
): boolean {
  const rowDiff = to.row - from.row;
  const colDiff = to.col - from.col;

  for (const wall of walls) {
    if (wall.orientation === 'horizontal') {
      // Mur horizontal bloque le mouvement vertical
      if (rowDiff !== 0) {
        const wallRow = rowDiff > 0 ? from.row + 1 : from.row;
        if (
          wall.row === wallRow &&
          (wall.col === from.col || wall.col === from.col - 1)
        ) {
          return true;
        }
      }
    } else {
      // Mur vertical bloque le mouvement horizontal
      if (colDiff !== 0) {
        const wallCol = colDiff > 0 ? from.col + 1 : from.col;
        if (
          wall.col === wallCol &&
          (wall.row === from.row || wall.row === from.row - 1)
        ) {
          return true;
        }
      }
    }
  }

  return false;
}

/**
 * Retourne les positions accessibles depuis une position donnée
 */
export function getAccessibleNeighbors(
  position: Position,
  walls: Wall[],
  opponentPosition?: Position
): Position[] {
  const neighbors: Position[] = [];
  const directions = [
    { row: -1, col: 0 }, // haut
    { row: 1, col: 0 },  // bas
    { row: 0, col: -1 }, // gauche
    { row: 0, col: 1 },  // droite
  ];

  for (const dir of directions) {
    const newPos: Position = {
      row: position.row + dir.row,
      col: position.col + dir.col,
    };

    // Vérifier les limites du plateau
    if (
      newPos.row < 0 ||
      newPos.row >= BOARD_SIZE ||
      newPos.col < 0 ||
      newPos.col >= BOARD_SIZE
    ) {
      continue;
    }

    // Vérifier si bloqué par un mur
    if (isBlockedByWall(position, newPos, walls)) {
      continue;
    }

    // Vérifier si l'adversaire est sur cette case
    if (
      opponentPosition &&
      newPos.row === opponentPosition.row &&
      newPos.col === opponentPosition.col
    ) {
      // Possibilité de sauter par-dessus l'adversaire
      const jumpPos: Position = {
        row: newPos.row + dir.row,
        col: newPos.col + dir.col,
      };

      // Saut direct si possible
      if (
        jumpPos.row >= 0 &&
        jumpPos.row < BOARD_SIZE &&
        jumpPos.col >= 0 &&
        jumpPos.col < BOARD_SIZE &&
        !isBlockedByWall(newPos, jumpPos, walls)
      ) {
        neighbors.push(jumpPos);
      } else {
        // Saut diagonal si le saut direct est bloqué
        const diagonalDirs =
          dir.row !== 0
            ? [{ row: 0, col: -1 }, { row: 0, col: 1 }]
            : [{ row: -1, col: 0 }, { row: 1, col: 0 }];

        for (const diagDir of diagonalDirs) {
          const diagPos: Position = {
            row: newPos.row + diagDir.row,
            col: newPos.col + diagDir.col,
          };

          if (
            diagPos.row >= 0 &&
            diagPos.row < BOARD_SIZE &&
            diagPos.col >= 0 &&
            diagPos.col < BOARD_SIZE &&
            !isBlockedByWall(newPos, diagPos, walls)
          ) {
            neighbors.push(diagPos);
          }
        }
      }
      continue;
    }

    neighbors.push(newPos);
  }

  return neighbors;
}

/**
 * BFS pour trouver un chemin vers la case cible exacte
 */
export function findPathToCell(
  startPosition: Position,
  targetRow: number,
  targetCol: number,
  walls: Wall[],
): Position[] | null {
  const visited = new Set<string>();
  const queue: Node[] = [{ position: startPosition, parent: null }];

  const posKey = (p: Position) => `${p.row},${p.col}`;
  visited.add(posKey(startPosition));

  while (queue.length > 0) {
    const current = queue.shift()!;

    if (current.position.row === targetRow && current.position.col === targetCol) {
      const path: Position[] = [];
      let node: Node | null = current;
      while (node !== null) {
        path.unshift(node.position);
        node = node.parent;
      }
      return path;
    }

    const neighbors = getAccessibleNeighbors(current.position, walls);

    for (const neighbor of neighbors) {
      const key = posKey(neighbor);
      if (!visited.has(key)) {
        visited.add(key);
        queue.push({ position: neighbor, parent: current });
      }
    }
  }

  return null;
}

/**
 * Vérifie si un joueur a un chemin vers sa case cible exacte
 */
export function hasPathToGoal(
  playerPosition: Position,
  targetRow: number,
  targetCol: number,
  walls: Wall[]
): boolean {
  return findPathToCell(playerPosition, targetRow, targetCol, walls) !== null;
}

/**
 * Retourne tous les déplacements valides pour un joueur
 */
export function getValidMoves(state: GameState, playerId: 1 | 2): Position[] {
  const player = state.players[playerId - 1];
  const opponent = state.players[playerId === 1 ? 1 : 0];

  return getAccessibleNeighbors(player.position, state.walls, opponent.position);
}

// Export pour éviter l'erreur TS6133
export type { Position };
